import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatButton } from '@angular/material/button';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { PaginationResult } from 'app/core/models/common.response';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;
    @ViewChild(CdkVirtualScrollViewport) private _virtualScroll: CdkVirtualScrollViewport;

    notifications: Notification[] = [];
    unreadCount: number = 0;
    pagination: PaginationResult;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    isLoading: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to notification changes
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) => {
                this.notifications = notifications;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to unread count
        this._notificationsService.unreadCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(count => {
                this.unreadCount = count;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to pagination
        this._notificationsService.pagination$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(pagination => {
                this.pagination = pagination;
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * After view init
     */
    ngAfterViewInit(): void {
        // Initial notification load
        this.isLoading = true;
        this._notificationsService.refreshNotifications()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.isLoading = false;
                this._changeDetectorRef.markForCheck();
            });
    }


    /**
    * On destroy
    */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
    * Open the notifications panel
    */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
    * Handle scroll event for loading more notifications
    */
    onScroll(): void {
        if (this._notificationsService.canLoadMore && !this._notificationsService.isLoading) {
            this.isLoading = true;
            this._notificationsService.loadMore().subscribe(() => {
                this.isLoading = false;
                this._changeDetectorRef.markForCheck();
            });
        }
    }


    /**
      * Close the notifications panel
      */
    closePanel(): void {
        if (this._overlayRef) {
            this._overlayRef.detach();
        }
    }

    /**
      * Mark notification as read
      */
    markAsRead(notification: Notification): void {
        // Return if notification is already read
        if (notification.isRead) {
            return;
        }

        this._notificationsService.markAsRead(notification.id).subscribe();
    }
    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        // Mark all as read
        this._notificationsService.markAllAsRead().subscribe();
    }

    // /**
    //  * Toggle read status of the given notification
    //  */
    // toggleRead(notification: Notification): void
    // {
    //     // Toggle the read status
    //     notification.read = !notification.read;

    //     // Update the notification
    //     this._notificationsService.update(notification.id, notification).subscribe();
    // }

    // /**
    //  * Delete the given notification
    //  */
    // delete(notification: Notification): void
    // {
    //     // Delete the notification
    //     this._notificationsService.delete(notification.id).subscribe();
    // }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top'
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom'
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top'
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }
                ])
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    formatLocalTime(utcTime: Date | string): string {
        if (!utcTime) {
            return '';
        }

        let date: Date;

        if (typeof utcTime === 'string') {
            if (!utcTime.endsWith('Z') && !utcTime.slice(-6).includes('+') && !utcTime.slice(-6).includes('-')) {
                utcTime += 'Z';
            }
        }

        date = new Date(utcTime);

        const timeString = date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });

        const dateString = date.toLocaleDateString('en-IN', {
            month: 'short',
            day: 'numeric'
        });

        return `${dateString}, ${timeString}`;
    }

    // /**
    //  * Calculate the unread count
    //  *
    //  * @private
    //  */
    // private _calculateUnreadCount(): void
    // {
    //     let count = 0;

    //     if ( this.notifications && this.notifications.length )
    //     {
    //         count = this.notifications.filter(notification => !notification.read).length;
    //     }

    //     this.unreadCount = count;
    // }
}
